import React, { useState, useEffect, useRef } from 'react';
import { Zap, Shield, Heart, ArrowRight, Users, Globe, Sparkles, Smartphone, MessageCircle, UserPlus, Menu, X } from 'lucide-react';
import SEO from '../components/SEO';
import './Landing.css';
import { useNavigate } from 'react-router-dom';

export default function OptimizedRazChatzLanding() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);

    
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    const slideTimer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % 3);
    }, 4000);

    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      clearTimeout(timer);
      clearInterval(slideTimer);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleSignin = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    navigate('/signup'); // Assuming you want to navigate to the signup page
  };

  const handleRegistration = () => {
    navigate('/signup'); // Assuming you want to navigate to the signup page
  };

  const features = [
    { 
      icon: <Zap className="feature-icon" />, 
      title: "Lightning Fast Chatz", 
      desc: "Real-time messaging with zero lag and instant delivery across all devices",
      gradient: "gradient-yellow"
    },
    { 
      icon: <Shield className="feature-icon" />, 
      title: "Secure & Private", 
      desc: "End-to-end encryption for all your conversations with advanced security protocols",
      gradient: "gradient-emerald"
    },
    { 
      icon: <Smartphone className="feature-icon" />, 
      title: "Mobile Device Ready", 
      desc: "Optimized for smartphone and tablet usage with responsive design",
      gradient: "gradient-blue"
    },
    { 
      icon: <Globe className="feature-icon" />, 
      title: "International Chat Rooms", 
      desc: "Connect with users worldwide through our international chat rooms and communities",
      gradient: "gradient-cyan"
    },
    { 
      icon: <Users className="feature-icon" />, 
      title: "Meet Strangers Safely", 
      desc: "Discover new connections and chat with strangers in a secure environment",
      gradient: "gradient-violet"
    },
    { 
      icon: <UserPlus className="feature-icon" />, 
      title: "Guest Chatting", 
      desc: "Start conversations immediately with guest chatting - no registration required initially",
      gradient: "gradient-pink"
    }
  ];

  const testimonials = [
    { name: "Sarah Chen", role: "Marketing Director", quote: "RazChatz transformed how our team communicates. The interface is incredibly intuitive and the international chat rooms feature is amazing!" },
    { name: "Marcus Johnson", role: "Tech Enthusiast", quote: "Finally, a chat app that's both beautiful and lightning fast. The mobile device experience is flawless!" },
    { name: "Emma Rodriguez", role: "Content Creator", quote: "The guest chatting feature and preference settings make it so easy to connect with my global community!" }
  ];

  const floatingElements = Array.from({ length: 15 }, (_, i) => (
    <div
      key={i}
      className="floating-element"
      style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${4 + Math.random() * 8}px`,
        height: `${4 + Math.random() * 8}px`,
        background: `hsl(${180 + Math.random() * 180}, 70%, 60%)`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${3 + Math.random() * 4}s`,
      }}
    />
  ));

  return (
    <div className="landing-container">
      <SEO 
        title="RazChatz - Modern Secure Chat Platform | Connect Globally"
        description="RazChatz is a lightning-fast, secure chat platform for connecting with people worldwide. Features include international chat rooms, guest chatting, mobile support, and end-to-end encryption."
        keywords="razchatz, chat app, secure messaging, international chat, mobile chat, guest chatting, real-time messaging, online chat, chat platform, Mohammad Razak A, instant messaging, group chat, private chat, encrypted chat"
        canonical="https://razchatz.netlify.app/"
        ogTitle="RazChatz - Connect. Chat. Thrive."
        ogDescription="Experience the future of communication with RazChatz. Lightning-fast, secure, and mobile-optimized chat platform connecting people worldwide."
      />
      {/* Navigation Bar */}
      <nav className="landing-navbar">
        <div className="navbar-container">
          <div className="navbar-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="./logo.png" alt="RazChatz" className="navbar-logo-img" />
            <span className="navbar-brand">RazChatz</span>
          </div>
          
          <div className={`navbar-links ${mobileMenuOpen ? 'navbar-links-mobile-open' : ''}`}>
            <a href="#home" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); }}>Home</a>
            <a href="#features" onClick={() => { document.querySelector('.features-section')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}>Features</a>
            <a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); setMobileMenuOpen(false); }}>About</a>
            <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); setMobileMenuOpen(false); }}>Privacy</a>
            <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); setMobileMenuOpen(false); }}>Terms</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); setMobileMenuOpen(false); }}>Contact</a>
            <button className="navbar-btn-login" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Login</button>
            <button className="navbar-btn-signup" onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}>Sign Up</button>
          </div>

          <button className="navbar-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Dynamic Background */}
      <div 
        className="dynamic-background"
        style={{
          background: `
            radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(59,130,246,0.15) 0%, transparent 50%),
            radial-gradient(circle at ${100-mousePosition.x}% ${100-mousePosition.y}%, rgba(147,51,234,0.15) 0%, transparent 50%)
          `,
        }}
      />
      
      {/* Floating Animation Elements */}
      <div className="floating-container">
        {floatingElements}
      </div>

      {/* Animated Grid Pattern */}
      <div className="grid-pattern" />

      {/* Hero Section */}
      <section ref={heroRef} className="hero-section">
        <div className={`hero-content ${isLoaded ? 'loaded' : ''}`}>
          
          {/* Logo/Brand */}
          <div className="logo-container">
            <div className="logo-wrapper">
              <div className="logo-circle">
                <img 
                  src="./logo.png" 
                  alt="RazChatz Logo" 
                  className="logo-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span className="logo-fallback">RC</span>
              </div>
              <div className="logo-glow" />
            </div>
          </div>

          {/* Main Headline */}
          <h1 className={`main-headline ${isLoaded ? 'loaded' : ''}`}>
            Welcome to <span className="highlight-cyan">RazChatz</span>
            <br />
            <span className="headline-connect">Connect.</span>
            <span className="headline-chat"> Chatz.</span>
            <span className="headline-thrive"> Thrive.</span>
          </h1>

          {/* Subtitle */}
          <p className={`subtitle ${isLoaded ? 'loaded' : ''}`}>
            Experience the future of social connection with our revolutionary platform. 
            Whether you're using a <span className="highlight-cyan">smartphone</span> or desktop, 
            connect with <span className="highlight-purple">strangers</span> worldwide through 
            <span className="highlight-emerald"> international chat rooms</span> or enjoy 
            <span className="highlight-pink"> guest chatting</span> with complete preference control.
          </p>

          {/* CTA Buttons */}
          <div className={`cta-buttons ${isLoaded ? 'loaded' : ''}`}>
            <button className="btn-primary" onClick={handleGetStarted}>
              <span className="btn-content">
                Start Chatting Now
                <ArrowRight className="btn-arrow" />
              </span>
              <div className="btn-gradient-overlay"></div>
              <div className="btn-shine"></div>
            </button>
            
            <button className="btn-secondary" onClick={handleSignin}>
              Login to you account
            </button>
          </div>

          {/* Stats */}
          <div className={`stats-grid ${isLoaded ? 'loaded' : ''}`}>
            {[
              { number: "20+", label: "Active Users" },
              { number: "99.9%", label: "Mobile Device Compatible" },
              { number: "24/7", label: "Global Support" }
            ].map((stat, i) => (
              <div key={i} className="stat-item">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="features-title">Why Choose Raz Chatz?</h2>
          <p className="features-subtitle">
            Experience the perfect blend of power, elegance, and innovation in every feature we deliver. 
            Our platform is designed to meet your communication preference while ensuring seamless 
            connectivity across all mobile device types.
          </p>
          
          <div className="features-grid">
            {features.map((feature, i) => (
              <div key={i} className="feature-card" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="feature-overlay"></div>
                <div className={`feature-icon-wrapper ${feature.gradient}`}>
                  {feature.icon}
                </div>
                <h3 className="feature-title" dangerouslySetInnerHTML={{ __html: feature.title }}></h3>
                <p className="feature-description">{feature.desc}</p>
                <div className={`feature-decoration ${feature.gradient}`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="how-it-works-container">
          <h2 className="how-it-works-title">How Raz Chatz Works</h2>
          <div className="steps-grid">
            
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="step-title">Quick Registration</h3>
              <p className="step-description">
                Sign up in seconds or use guest chatting to start immediately. 
                Set your preference for the type of conversations you want.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="step-title">Choose Your Space</h3>
              <p className="step-description">
                Join international chat rooms or create private conversations. 
                Our platform works perfectly on any smartphone or mobile device.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="step-title">Connect & Chat</h3>
              <p className="step-description">
                Meet strangers from around the world or reconnect with friends. 
                Experience lightning-fast messaging with complete security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <h2 className="testimonials-title">
            Loved by <span className="testimonials-highlight">Global Users</span>
          </h2>
          
          <div className="testimonials-wrapper">
            <div className="testimonials-carousel">
              <div 
                className="testimonials-track"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {testimonials.map((testimonial, i) => (
                  <div key={i} className="testimonial-slide">
                    <p className="testimonial-quote">"{testimonial.quote}"</p>
                    <div className="testimonial-name">{testimonial.name}</div>
                    <div className="testimonial-role">{testimonial.role}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="testimonials-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`testimonial-dot ${currentSlide === i ? 'active' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Features Section */}
      <section className="mobile-features-section">
        <div className="mobile-features-container">
          <h2 className="mobile-features-title">Perfect for Every Mobile Device</h2>
          <p className="mobile-features-subtitle">
            Whether you're on a smartphone, tablet, or desktop, Raz Chatz delivers 
            the same exceptional experience. Our responsive design ensures optimal 
            performance across all platforms and devices.
          </p>
          
          <div className="mobile-features-grid">
            <div className="mobile-feature-item">
              <Smartphone className="mobile-feature-icon" />
              <h3>Smartphone Optimized</h3>
              <p>Native-like experience on all smartphone platforms with touch-friendly interface</p>
            </div>
            
            <div className="mobile-feature-item">
              <MessageCircle className="mobile-feature-icon" />
              <h3>Instant Notifications</h3>
              <p>Never miss a message with smart notifications that respect your preference settings</p>
            </div>
            
            <div className="mobile-feature-item">
              <Globe className="mobile-feature-icon" />
              <h3>Offline Capability</h3>
              <p>Continue reading messages even when your mobile device is offline</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="final-cta-container">
          <h2 className="final-cta-title">
            Ready to Start <span className="final-cta-highlight">Your Chatz Journey</span>?
          </h2>
          <p className="final-cta-subtitle">
            Join the Raz Chatz community today and experience the future of messaging. 
            Connect with strangers, join international chat rooms, or start with guest chatting. 
            Your communication preference, your way.
          </p>
          
          <div className="final-cta-buttons">
            <button className="final-cta-button" onClick={handleRegistration}>
              <span className="final-cta-content">
                Join Raz Chatz Now
                <Sparkles className="final-cta-icon" />
              </span>
              <div className="final-cta-gradient"></div>
              <div className="final-cta-shine"></div>
            </button>
            
            <button className="final-cta-secondary" onClick={() => console.log('Try guest chat')}>
              Try Guest Chatting
            </button>
          </div>
        </div>
      </section>

              

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand-section">
              <h3 className="footer-brand-title">Raz Chatz</h3>
              <p className="footer-brand-desc">
                Connecting people worldwide through innovative chatz technology. 
                Experience seamless communication on any mobile device.
              </p>
            </div>
            
            <div className="footer-links-section">
              <div className="footer-links-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>About Us</a></li>
                  <li><a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a></li>
                  <li><a href="#features" onClick={() => document.querySelector('.features-section')?.scrollIntoView({ behavior: 'smooth' })}>Features</a></li>
                </ul>
              </div>
              
              <div className="footer-links-column">
                <h4>Legal</h4>
                <ul>
                  <li><a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy Policy</a></li>
                  <li><a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>Terms of Service</a></li>
                </ul>
              </div>
              
              <div className="footer-links-column">
                <h4>Get Started</h4>
                <ul>
                  <li><a href="/signup" onClick={(e) => { e.preventDefault(); navigate('/signup'); }}>Sign Up</a></li>
                  <li><a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Login</a></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p className="footer-text">
              &copy; 2025 <span className="footer-brand">Raz Chatz</span>
              {' '}&mdash; Created with <Heart className="footer-heart" /> BY <div class="badge-base LI-profile-badge" data-locale="en_US" data-size="medium" data-theme="dark" data-type="VERTICAL" data-vanity="mohammadrazak-abdulrasheeth" data-version="v1"><a class="badge-base__link LI-simple-link" href="https://in.linkedin.com/in/mohammadrazak-abdulrasheeth?trk=profile-badge" 
              style={{ color: 'yellow', textDecoration: 'none'  }}  >Mohammad Razak A</a></div>
              
            </p>
          </div>
        </div>
      </footer>

      {/* Additional Glow Effects */}
      <div className="glow-effect glow-cyan"></div>
      <div className="glow-effect glow-purple"></div>
      <div className="glow-effect glow-pink"></div>
    </div>
  );
}